import {
  DeleteIconData,
  RestoreFromTrashIconData,
  StarFillIconData,
  StarIconData,
} from "@meiden-connect/material-symbols";
import { Button, useThemeMode } from "@meiden-connect/ui";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useState, type MouseEvent } from "react";
import {
  useCanvasApi,
  type AllCanvasItem,
  type FavoriteCanvasItem,
  type RecentCanvasItem,
  type TrashedCanvasItem,
} from "../../lib/canvas-api";
import { generateCanvasId } from "../../lib/canvas-id";
import { normalizeCanvasTitle } from "../../lib/canvas-title";
import { formatJstDateTime } from "../../lib/datetime";
import { navigate } from "../../router";
import {
  CanvasCardContextMenu,
  type CanvasCardContextMenuItem,
} from "./canvas-card-context-menu";
import { RecentCanvasCard } from "./recent-canvas-card";
import {
  useDashboardCanvasActions,
  type DashboardCanvasActionTarget,
} from "./use-dashboard-canvas-actions";

type CardOrigin = "favorites" | "recent" | "all" | "trash";

interface OpenMenu {
  readonly origin: CardOrigin;
  readonly target: DashboardCanvasActionTarget;
  readonly isFavorite?: boolean | undefined;
  readonly clientX: number;
  readonly clientY: number;
  readonly anchor: HTMLButtonElement;
}

// ダッシュボード（TOP）画面。
// 能動的に選んだお気に入りを優先し、右クリックから整理操作を行えるようにする。
export function DashboardView(): JSX.Element {
  const canvasApi = useCanvasApi();
  const themeMode = useThemeMode();
  const [openMenu, setOpenMenu] = useState<OpenMenu>();
  const actions = useDashboardCanvasActions();
  // ヒーロー画像はリンクプレビュー用の OGP（/ogp.png）とは別の、テーマごとの紹介画像を表示する。
  const heroImageSrc = themeMode === "dark" ? "/dashboard-hero-dark.png" : "/dashboard-hero.png";

  const recentCanvasesQuery = useQuery({
    queryKey: ["recent-canvases"],
    queryFn: canvasApi.fetchRecentCanvases,
    refetchOnMount: true,
  });
  const favoritesQuery = useQuery({
    queryKey: ["favorites"],
    queryFn: canvasApi.fetchFavorites,
    refetchOnMount: true,
  });
  const meQuery = useQuery({ queryKey: ["me"], queryFn: canvasApi.fetchMe });
  const canRead = meQuery.data?.actor.permissions.canRead ?? false;
  const canWrite = meQuery.data?.actor.permissions.canWrite ?? false;
  const canAdmin = meQuery.data?.actor.permissions.canAdmin ?? false;
  const allCanvasesQuery = useQuery({
    queryKey: ["all-canvases"],
    queryFn: canvasApi.fetchAllCanvases,
    enabled: canAdmin,
    refetchOnMount: true,
  });
  const trashQuery = useQuery({
    queryKey: ["trash"],
    queryFn: canvasApi.fetchTrash,
    refetchOnMount: true,
  });

  const recentCanvases: readonly RecentCanvasItem[] = recentCanvasesQuery.data?.items ?? [];
  const favorites: readonly FavoriteCanvasItem[] = favoritesQuery.data?.items ?? [];
  const allCanvases: readonly AllCanvasItem[] = allCanvasesQuery.data?.items ?? [];
  const trash: readonly TrashedCanvasItem[] = trashQuery.data?.items ?? [];
  const favoriteCanvasIds = useMemo(
    () => new Set(favorites.map((canvas) => canvas.canvasId)),
    [favorites],
  );
  const favoriteStateKnown = favoritesQuery.isSuccess;
  // カードのメニュー項目を決める権限（/api/me）とお気に入り状態（/api/me/favorites）の取得が成功したか。
  // 権限が未確定の間の右クリックではメニューを開かず、お気に入り状態が未確定の間はお気に入りの項目を出さない
  // （canvas-trash-and-card-context-menu §8.2）ため、E2E はこの属性が true（両方の取得が成功）になるのを
  // 待ってから右クリックする。表示と挙動には使わない。
  const cardMenuReady = meQuery.isSuccess && favoriteStateKnown;

  const menuItems = useMemo<readonly CanvasCardContextMenuItem[]>(() => {
    if (!openMenu) return [];
    if (openMenu.origin === "trash") {
      return canWrite
        ? [
            {
              key: "restore",
              label: "ゴミ箱から戻す",
              iconData: RestoreFromTrashIconData,
              disabled: actions.busy,
              onSelect: () => void actions.restore(openMenu.target),
            },
          ]
        : [];
    }
    const items: CanvasCardContextMenuItem[] = [];
    if (canRead && favoriteStateKnown && openMenu.isFavorite !== undefined) {
      items.push(
        openMenu.isFavorite
          ? {
              key: "remove-favorite",
              label: "お気に入りを解除",
              iconData: StarFillIconData,
              disabled: actions.busy,
              onSelect: () => void actions.removeFavorite(openMenu.target),
            }
          : {
              key: "add-favorite",
              label: "お気に入りに追加",
              iconData: StarIconData,
              disabled: actions.busy,
              onSelect: () => void actions.addFavorite(openMenu.target),
            },
      );
    }
    if (canWrite) {
      items.push({
        key: "trash",
        label: "ゴミ箱へ移動",
        iconData: DeleteIconData,
        danger: true,
        disabled: actions.busy,
        onSelect: () => void actions.trash(openMenu.target),
      });
    }
    return items;
  }, [actions, canRead, canWrite, favoriteStateKnown, openMenu]);

  const openContextMenu = (
    event: MouseEvent<HTMLButtonElement>,
    origin: CardOrigin,
    target: DashboardCanvasActionTarget,
  ): void => {
    const isFavorite =
      origin === "trash" || !favoriteStateKnown
        ? undefined
        : favoriteCanvasIds.has(target.canvasId);
    const hasMenuItem = origin === "trash" ? canWrite : canWrite || (canRead && isFavorite !== undefined);
    if (!hasMenuItem) return;
    event.preventDefault();
    setOpenMenu({
      origin,
      target,
      isFavorite,
      clientX: event.clientX,
      clientY: event.clientY,
      anchor: event.currentTarget,
    });
  };

  const handleCreate = (): void => navigate(`/c/${generateCanvasId()}`);

  return (
    <div
      className="dashboard-content"
      data-testid="dashboard-root"
      data-card-menu-ready={cardMenuReady ? "true" : "false"}
    >
      <section className="dashboard-hero-card">
        <div className="dashboard-hero-copy">
          <p className="dashboard-eyebrow">MEIDEN CONNECT Collaborative Canvas</p>
          <h1>Connect Canvas へようこそ</h1>
          <p>
            リファインメントやブレスト、振り返りなど、チームの議論を共同編集で進めるためのお絵かきツールです。
            新規キャンバスを作成するか、最近使ったキャンバスを開いてください。
          </p>
          <div className="dashboard-hero-actions">
            <Button type="button" variant="action emphasis" onClick={handleCreate}>
              + 新規キャンバスを作成
            </Button>
            <Button type="button" variant="action" onClick={() => navigate("/mcp-setup")}>
              AI（MCP）から操作する
            </Button>
          </div>
        </div>
        <picture className="dashboard-hero-image-frame">
          <img alt="Connect Canvas の紹介画像" src={heroImageSrc} />
        </picture>
      </section>

      {favorites.length > 0 ? (
        <section className="dashboard-card" data-testid="dashboard-favorites">
          <h2>お気に入り</h2>
          <div className="recent-canvas-grid">
            {favorites.map((canvas) => (
              <RecentCanvasCard
                key={canvas.canvasId}
                canvasId={canvas.canvasId}
                title={normalizeCanvasTitle(canvas.title)}
                subtitle={formatJstDateTime(canvas.updatedAt)}
                thumbnailUrl={canvas.thumbnailUrl}
                onContextMenu={(event) =>
                  openContextMenu(event, "favorites", toActionTarget(canvas, canvas.updatedAt))
                }
              />
            ))}
          </div>
        </section>
      ) : null}

      <section className="dashboard-card" data-testid="dashboard-recent">
        <h2>最近使ったキャンバス</h2>
        {recentCanvasesQuery.isLoading ? (
          <p className="dashboard-muted">読み込み中…</p>
        ) : recentCanvasesQuery.isError ? (
          <p className="dashboard-danger">履歴の取得に失敗しました。時間をおいて再読み込みしてください。</p>
        ) : recentCanvases.length === 0 ? (
          <p className="dashboard-muted">まだ最近使ったキャンバスはありません。</p>
        ) : (
          <div className="recent-canvas-grid">
            {recentCanvases.map((canvas) => (
              <RecentCanvasCard
                key={canvas.canvasId}
                canvasId={canvas.canvasId}
                title={normalizeCanvasTitle(canvas.title)}
                subtitle={formatJstDateTime(canvas.lastOpenedAt)}
                thumbnailUrl={canvas.thumbnailUrl}
                onContextMenu={(event) =>
                  openContextMenu(event, "recent", toActionTarget(canvas, canvas.lastOpenedAt))
                }
              />
            ))}
          </div>
        )}
      </section>

      {canAdmin ? (
        <section className="dashboard-card" data-testid="dashboard-all-canvases">
          <h2>全キャンバス一覧</h2>
          {allCanvasesQuery.isLoading ? (
            <p className="dashboard-muted">読み込み中…</p>
          ) : allCanvasesQuery.isError ? (
            <p className="dashboard-danger">
              全キャンバス一覧の取得に失敗しました。時間をおいて再読み込みしてください。
            </p>
          ) : allCanvases.length === 0 ? (
            <p className="dashboard-muted">キャンバスはまだありません。</p>
          ) : (
            <div className="recent-canvas-grid">
              {allCanvases.map((canvas) => (
                <RecentCanvasCard
                  key={canvas.canvasId}
                  canvasId={canvas.canvasId}
                  title={normalizeCanvasTitle(canvas.title)}
                  subtitle={formatJstDateTime(canvas.updatedAt)}
                  thumbnailUrl={canvas.thumbnailUrl}
                  onContextMenu={(event) =>
                    openContextMenu(event, "all", toActionTarget(canvas, canvas.updatedAt))
                  }
                />
              ))}
            </div>
          )}
        </section>
      ) : null}

      {trash.length > 0 ? (
        <section className="dashboard-card" data-testid="dashboard-trash">
          <h2>ゴミ箱</h2>
          <div className="recent-canvas-grid">
            {trash.map((canvas) => (
              <RecentCanvasCard
                key={canvas.canvasId}
                canvasId={canvas.canvasId}
                title={normalizeCanvasTitle(canvas.title)}
                subtitle={`ゴミ箱へ移動 ${formatJstDateTime(canvas.trashedAt)}`}
                thumbnailUrl={canvas.thumbnailUrl}
                onContextMenu={(event) =>
                  openContextMenu(event, "trash", toActionTarget(canvas, canvas.trashedAt))
                }
              />
            ))}
          </div>
        </section>
      ) : null}

      {openMenu && menuItems.length > 0 ? (
        <CanvasCardContextMenu
          clientX={openMenu.clientX}
          clientY={openMenu.clientY}
          anchor={openMenu.anchor}
          items={menuItems}
          themeMode={themeMode}
          onClose={() => setOpenMenu(undefined)}
        />
      ) : null}
    </div>
  );
}

function toActionTarget(
  canvas: {
    readonly canvasId: string;
    readonly title: string;
    readonly thumbnailKey?: string | undefined;
    readonly thumbnailUrl?: string | undefined;
  },
  updatedAt: string,
): DashboardCanvasActionTarget {
  return { ...canvas, updatedAt };
}
